import React, { useState } from "react"
import classNames from "classnames"
import { allPieces } from "@/utils/data"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setPreferredPieceId } from "@/store/globalSlice"
import {
  savePreferredPieceIdToAccount,
  setCachedPreferredPieceId,
} from "@/utils/piecePreference"
import { getUserCreds } from "@/utils/utils"

interface PiecePickerProps {
  /** Extra classes for the grid, so each host can set its own column count. */
  className?: string
  /** Test id prefix, so the in-game modal and the profile page stay distinct. */
  testIdPrefix?: string
  /**
   * Fired after the choice is applied, with the new piece (or undefined when it
   * was cleared). Hosts that open the picker in a popover use it to close
   * themselves; the panels that live on a page leave it out and stay put.
   */
  onSelect?: (pieceId: string | undefined) => void
}

/**
 * Pick the token you play as. Shared by the in-game settings modal and the
 * profile settings page — both write the same local preference, which every
 * future game then honours.
 *
 * Worth knowing while reading this: the choice is not reserved with the server.
 * Everyone at the table can pick the same piece, because each player only ever
 * changes what *they* see for their own seat (see utils/piecePreference.ts).
 */
const PiecePicker: React.FC<PiecePickerProps> = ({
  className,
  testIdPrefix = "settings",
  onSelect,
}) => {
  const dispatch = useAppDispatch()
  const preferredPieceId = useAppSelector(
    (state) => state.global.preferredPieceId
  )
  const [saveFailed, setSaveFailed] = useState(false)

  // Applied straight away — in a running game the board and the player cards
  // change over on the spot — then saved on the account so it comes back on any
  // device. The local copy is written first and kept even if the save fails, so
  // a dropped connection costs you the sync, not the piece you just picked.
  const handleSelect = (pieceId: string | undefined) => {
    const email = getUserCreds().email
    setCachedPreferredPieceId(email, pieceId)
    dispatch(setPreferredPieceId(pieceId))
    savePreferredPieceIdToAccount(email, pieceId).then((saved) =>
      setSaveFailed(!saved)
    )
    onSelect?.(pieceId)
  }

  return (
    <div>
      {/* `mobile:` px steps throughout: below 1000px index.css halves the root
          font size, so the rem sizes here come out at half what they read as —
          `text-sm` is 7px and a tile's piece image 24px. */}
      <p className="mb-4 font-poppins text-sm text-white/60 mobile:mb-[10px] mobile:text-[11px] mobile:leading-snug">
        Play as the piece you like. Saved to your account, so you'll get it in
        every game on any device — and the other players keep seeing their own
        choice, so nobody has to give theirs up.
      </p>
      <div
        className={classNames(
          "grid grid-cols-3 gap-4 sm:grid-cols-4 lg:grid-cols-6 mobile:gap-[8px]",
          className
        )}
      >
        {allPieces.map((piece, index) => {
          const isSelected = piece.id === preferredPieceId
          return (
            <button
              key={piece.id}
              onClick={() => handleSelect(piece.id)}
              data-testid={`${testIdPrefix}.piece-option.${piece.id}`}
              title={`Play as piece ${index + 1}`}
              className={classNames(
                "group relative flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 bg-black/30 p-2 transition-all mobile:gap-[2px] mobile:rounded-[10px] mobile:p-[6px]",
                isSelected
                  ? "scale-[1.02] border-amber-400 ring-2 ring-amber-400/50"
                  : "border-white/10 hover:border-white/40"
              )}
            >
              <img
                src={piece.imgSrc}
                alt={`Piece ${index + 1}`}
                className="h-10 w-10 object-contain sm:h-12 sm:w-12 mobile:h-[34px] mobile:w-[34px]"
              />
              <span className="font-poppins text-[10px] font-medium text-white/70 mobile:text-[9px]">
                Piece {index + 1}
              </span>
              {isSelected && (
                <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-black shadow mobile:right-[3px] mobile:top-[3px] mobile:h-[14px] mobile:w-[14px]">
                  <i className="fa-solid fa-check text-[10px]"></i>
                </span>
              )}
            </button>
          )
        })}
      </div>
      {/* Clearing the choice is its own control rather than a toggle on the
          selected tile, so a second click on your piece never un-picks it by
          accident. */}
      <button
        onClick={() => handleSelect(undefined)}
        disabled={!preferredPieceId}
        data-testid={`${testIdPrefix}.piece-option.clear`}
        className={classNames(
          "mt-4 flex items-center gap-2 rounded-lg px-3 py-2 font-poppins text-xs font-semibold transition-colors mobile:mt-[10px] mobile:gap-[6px] mobile:px-[10px] mobile:py-[7px] mobile:text-[11px]",
          preferredPieceId
            ? "text-white/70 hover:bg-white/10 hover:text-white"
            : "cursor-not-allowed text-white/30"
        )}
      >
        <i className="fa-solid fa-rotate-left"></i>
        Use whichever piece the game gives me
      </button>

      {saveFailed && (
        <p
          className="mt-2 font-poppins text-xs text-amber-300 mobile:mt-[6px] mobile:text-[11px]"
          data-testid={`${testIdPrefix}.piece-save-failed`}
        >
          Couldn't save this to your account — it's applied here, but another
          device won't pick it up until you choose again.
        </p>
      )}
    </div>
  )
}

export default PiecePicker
